import { createContext, useContext } from 'react';

/**
 * Bumped once per successful location-enrichment (autofill) application (see
 * `LocationInformationStep`'s `onEnrichmentApplied`). Any field that autofill
 * can populate programmatically (rather than via a native input event) reads
 * this version — via `useEnrichmentVersion()` directly, or indirectly through
 * `AutofillTextField` — and uses it as part of a remount `key`. Remounting
 * forces MUI to re-run its own mount-time "filled" detection against the new
 * value, so the field's label shrinks correctly instead of relying on a native
 * change event that autofill never fires.
 *
 * Provided once by `CreateLocationWizard`; consumed by any step/field without
 * needing it threaded through every intermediate component's props.
 *
 * Defaults to `null` (rather than `0`) so a missing `EnrichmentVersionProvider`
 * fails loudly via `useEnrichmentVersion()` instead of silently behaving like
 * version `0` forever — which would stop affected fields from remounting on
 * autofill and quietly regress the label-shrink fix with no visible error.
 */
const EnrichmentVersionContext = createContext<number | null>(null);

export const EnrichmentVersionProvider = EnrichmentVersionContext.Provider;

/**
 * @throws if called outside an `EnrichmentVersionProvider` — this indicates a
 * wiring mistake (a new consumer added without wrapping it in the provider),
 * not a valid runtime state.
 */
export const useEnrichmentVersion = (): number => {
  const version = useContext(EnrichmentVersionContext);
  if (version === null) {
    throw new Error(
      'useEnrichmentVersion() must be called within an EnrichmentVersionProvider ' +
        '(see CreateLocationWizard.tsx). Wrap the consuming component tree in ' +
        '<EnrichmentVersionProvider value={enrichmentVersion}>.'
    );
  }
  return version;
};

export default EnrichmentVersionContext;
